import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select
} from '@material-ui/core';
import { useEffect, useState, useRef } from 'react';
import {
  IHabitatServiceObj,
  IHabitatServiceTypeProp,
  ILocationServiceObj,
  IObservationServiceObj,
} from '../../../shared/apiServices/IApiInterfaces';
import { HabitatTypeProps } from '../../../shared/enums/HabitatTypeProps';
import { ObservationType } from '../../../shared/enums/ObservationType';
import { RouteNames } from '../../../shared/enums/RouteNames';
import sharedHistory from '../../../shared/History';
import Toast from '../../../shared/Notifications/Toast';
import ManageHabitatsApiHelpers from '../../views/manageHabitats/ManageHabitatsApiHelpers';
import './ObservationsForm.scss';
import ObservationsFormApiHelpers, {
  IWildLifeServiceObj,
} from './ObservationsFormAPIHelpers';
import { Add, Clear, TrashCan, Warning } from '../../../constants/Icons';
import { Photo } from '../Photo/Photo';
import { Preloader } from '../../../shared/Preloader/Preloader';
import { url } from 'inspector';
import { isObservationOwner } from '../../views/savedObservations/saveObbservationHelper';
import { useTypedSelector } from '../../../hooks/useTypedSelectors';

export type ObservationsFormType = 'edit' | 'create';

interface IObservationsFormProps {
  type: ObservationsFormType;
  observationObj?: IObservationServiceObj;
  userEmail?: string;
  isShowTrashCan?: boolean;
}

interface IExistingPhoto {
  url: string;
  id: string;
}

interface IToSavePhoto {
  url: string;
  isValid: boolean;
  photoFile: File;
}

const MAX_PHOTO_SIZE = 4000000;

export const ObservationsForm = ({
  type,
  observationObj,
  userEmail,
  isShowTrashCan,
}: IObservationsFormProps) => {
  const user = useTypedSelector((store) => store.User?.UserObject);
  const photoInput = useRef<HTMLInputElement>(null);
  const [photoURLs, setPhotoURLs] = useState<IToSavePhoto[]>([])
  const [existingPhotoURLs, setExistingPhotoURLs] = useState<IExistingPhoto[]>([])
  const [observation, setObservation] = useState<IObservationServiceObj>(
    observationObj ?? ({ observedBy: userEmail } as IObservationServiceObj)
  );
  const [locations, setLocations] = useState<ILocationServiceObj[]>([]);
  const [wildlife, setWildLife] = useState<IWildLifeServiceObj[]>();
  const [habitats, setHabitats] = useState<IHabitatServiceObj[]>();
  const [fledgedDate, setFledgedDate] = useState<string>(
    observationObj?.habitatTypeProps.find(
      (htp) => htp.name === HabitatTypeProps.FledgedDate
    )?.value ?? ''
  );
  const [daysOld, setDaysOld] = useState<string>(
    observationObj?.habitatTypeProps.find(
      (htp) => htp.name === HabitatTypeProps.YoungAge
    )?.value ?? '0'
  );
  const [fledged, setFledged] = useState<string>(
    observationObj?.habitatTypeProps.find(
      (htp) => htp.name === HabitatTypeProps.NumberOfFledged
    )?.value ?? '0'
  );
  const [young, setYoung] = useState<string>(
    observationObj?.habitatTypeProps.find(
      (htp) => htp.name === HabitatTypeProps.NumberOfYoung
    )?.value ?? '0'
  );
  const [eggs, setEggs] = useState<string>(
    observationObj?.quantity?.toString() ?? '0'
  );
  const [date, setDate] = useState<string>();
  const [time, setTime] = useState<string>();
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(false);

  const setDateTime = () => {
    let defaultDate = new Date().toISOString();

    if (type !== 'create' || observation?.date) {
      defaultDate = observation.date.toString();
    }

    const dateTimeArray = defaultDate.split('T');
    setDate(dateTimeArray[0]);

    const obsTime = dateTimeArray[1].split('Z')[0];
    setTime(obsTime);
  };

  const loadHabitats = async () => {
    const initialHabitats: IHabitatServiceObj[] =
      await ManageHabitatsApiHelpers.getAllHabitats();
    const birdBoxesForLoc = initialHabitats.filter(
      (h) =>
        h.locationId.toString() === observation.locationId.toString() &&
        h.type === 'birdbox'
    ).sort((a, b) => a.name > b.name ? 1 : -1);

    setHabitats(birdBoxesForLoc);
  };

  useEffect(() => {
    if (observation.locationId) loadHabitats();
  }, [observation.locationId]);

  useEffect(() => {
    const loadWildLife = async () => {
      const wildLife: IWildLifeServiceObj[] =
        await ObservationsFormApiHelpers.getAllWildlife();
      setWildLife(wildLife);
    };
    loadWildLife();
    setDateTime();

    if (type === 'create') {
      setObservationProperties('observedBy', userEmail);

      const fetchLocationData = async () => {
        const newLocations = await getLocations();
        setLocations(newLocations);
      };
      fetchLocationData();
    } else {
      loadHabitats();
    }
  }, []);

  useEffect(() => {
    if (observationObj && observationObj.photoUrls?.length) {
      for (const url in observationObj.photoUrls) {
        const entry: IExistingPhoto = {
          id: `photo-id-${url}`,
          url: observationObj.photoUrls[url]
        }

        setExistingPhotoURLs(prev => [...prev, entry])
      }
    }
  }, [observationObj]);

  const getLocations = async (): Promise<ILocationServiceObj[]> => {
    try {
      setIsLoading(true);
      const initialLocations: ILocationServiceObj[] =
        await ManageHabitatsApiHelpers.getAllHabitatLocations();
      return initialLocations;
    } catch (ex) {
      Toast.show('Error Getting Sites');
      console.error();
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const setObservationProperties = (key: string, value: any) => {
    // If every required property is set, then enable the save button
    setObservation((prevOb) => ({ ...prevOb, [key]: value }));
  };

  const firstCol = (
    <div className="generalData">
      <div className="row select">
        <Select
          labelId="birdbox-select-label"
          id="birdBoxSelect"
          label="Habitat"
          displayEmpty
          value={observation.habitatName ?? ''}
          style={{ minWidth: 300 }}
          onChange={(event) => {
            setObservationProperties(
              'habitatName',
              event.target.value as string
            );
            setObservationProperties(
              'habitatId',
              habitats?.find((h) => h.name === event.target.value)?.id
            );
          }}
        >
          <MenuItem value="" disabled>
            Choose habitat
          </MenuItem>
          {habitats?.map((h) => {
            return (
              <MenuItem key={h.id} value={h.name}>
                {h.name}
              </MenuItem>
            );
          })}
        </Select>
      </div>
      <div className="input-field edit row">
        <input
          disabled={type !== 'create' ?? false}
          type="date"
          id="date"
          value={date}
          style={{ maxWidth: '300px' }}
          onChange={(e) => {
            setDate(e.target.value)
          }}
        />
        <label htmlFor="date" className={type === 'create' ? 'active' : ''}>
          Date
        </label>
      </div>
      <div className="radio-btns row">
        <FormControl>
          <FormLabel id="obs-type-radio-buttons-group-label">
            Birdbox Observation Type
          </FormLabel>
          <RadioGroup
            aria-labelledby="obs-type-radio-buttons-group-label"
            value={observation.observationType ?? ''}
            name="radio-buttons-group"
            onChange={(event) =>
              setObservationProperties('observationType', event.target.value)
            }
          >
            <FormControlLabel
              value={ObservationType.EMPTY_BOX}
              control={<Radio />}
              label="Empty Box"
            />
            <FormControlLabel
              value={ObservationType.PARTIAL_NEST}
              control={<Radio />}
              label="Partial Nest"
            />
            <FormControlLabel
              value={ObservationType.COMPLETE_NEST}
              control={<Radio />}
              label="Complete Nest"
            />
          </RadioGroup>
        </FormControl>
      </div>
      <div className="row">
        <div className="observedBy">Observed By</div>
        <p>{observation.observedBy ?? 'CHANGE THIS'}</p>
      </div>
    </div>
  );

  const secondCol = (
    <div className="secondCol">
      <div className="row select">
        <Select
          id="wildlife-select"
          label="Species"
          value={observation.wildlifeName ?? ''}
          style={{ minWidth: 300 }}
          displayEmpty
          onChange={(event) => {
            setObservationProperties(
              'wildlifeName',
              event.target.value as string
            );
            setObservationProperties(
              'wildlifeId',
              wildlife?.find((w) => w.name === event.target.value)?.id
            );
          }}
        >
          <MenuItem value="" disabled>
            Choose wildlife
          </MenuItem>
          {wildlife?.map((w) => {
            return (
              <MenuItem key={w.id} value={w.name}>
                {w.name}
              </MenuItem>
            );
          })}
        </Select>
      </div>
      <div className="input-field row edit">
        <input
          disabled={type !== 'create' ?? false}
          type="time"
          id="time"
          value={time}
          name="obs-time"
          onChange={(e) => {
            setTime(e.target.value)
          }}
          style={{ maxWidth: '300px' }}
        />
        <label htmlFor="time" className={time ? 'active' : ''}>
          Time
        </label>
      </div>
      <div className="row">
        <div className="birdbox-props">Birdbox Properties</div>
        <div className="input-field edit">
          <input
            id="num-eggs"
            type="number"
            onChange={(event) => setEggs(event.target.value)}
            value={eggs}
            onKeyDown={(event) => {
              if (event.key === '.') {
                event.preventDefault();
              }
            }}
          ></input>
          <label htmlFor="num-eggs" className={eggs ? 'active' : ''}>
            Number of Eggs
          </label>
        </div>
        <div className="input-field edit">
          <input
            id="young"
            type="number"
            onChange={(event) => setYoung(event.target.value)}
            value={young}
            onKeyDown={(event) => {
              if (event.key === '.') {
                event.preventDefault();
              }
            }}
          ></input>
          <label htmlFor="young" className={young ? 'active' : ''}>
            Number of Young
          </label>
        </div>
        <div className="input-field edit">
          <input
            id="days"
            type="number"
            onChange={(event) => setDaysOld(event.target.value)}
            value={daysOld}
            onKeyDown={(event) => {
              if (event.key === '.') {
                event.preventDefault();
              }
            }}
          ></input>
          <label htmlFor="days" className={daysOld ? 'active' : ''}>
            Days (Optional)
          </label>
        </div>
        <div className="input-field edit">
          <input
            id="fledged"
            type="number"
            onChange={(event) => setFledged(event.target.value)}
            value={fledged}
            onKeyDown={(event) => {
              if (event.key === '.') {
                event.preventDefault();
              }
            }}
          ></input>
          <label htmlFor="fledged" className={fledged ? 'active' : ''}>
            Number of Fledged
          </label>
        </div>
        <div className="input-field edit">
          <input
            id="date-fledged"
            type="date"
            onChange={(event) => setFledgedDate(event.target.value)}
            value={fledgedDate}
          ></input>
          <label htmlFor="date-fledged" className={fledgedDate ? 'active' : ''}>
            Estimated Fledge date (Optional)
          </label>
        </div>
      </div>
    </div>
  );

  const handleAddPhotoClick = () => {
    photoInput?.current?.click();
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const photos: IToSavePhoto[] = Object.values(e.target.files).map((f) => {
        if (f.size > MAX_PHOTO_SIZE)
          Toast.show(`${f.name} exceeds the 4 MB limit.`);

        return {
          url: URL.createObjectURL(f),
          isValid: f.size < MAX_PHOTO_SIZE,
          photoFile: f
        }
      });

      setPhotoURLs([
        ...photoURLs,
        ...photos,
      ]);
    }
  };

  const handleClearPhotos = () => {
    setPhotoURLs([]);
  };

  const handleRemovePhoto = (indexToRemove: number) => {
    setPhotoURLs(prevPhotoURLs => prevPhotoURLs.filter((_, index) => index !== indexToRemove));
  };

  const handleDeletePhoto = (e: any) => {
    const existingPhotoArray: IExistingPhoto[] = existingPhotoURLs;
    const existingPhotoDiv = e.target.parentNode.parentNode.parentNode;
    const urlIndex = existingPhotoArray.findIndex(x => x.id === existingPhotoDiv.getAttribute('data-key'));

    existingPhotoArray.splice(urlIndex, 1);
    setExistingPhotoURLs([...existingPhotoArray]);
  }

  const thirdCol = (
    <div className="third-col">
      <div className="row photos">
        <input
          ref={photoInput}
          id="photo-input"
          accept="image/*"
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handlePhotoChange}
          value={[]}
        />
        <h6 className="row center-align">{'Add Photo (Optional)'}</h6>
        <div className="row">
          {existingPhotoURLs?.map((data) => (
            <div key={data.url} data-key={data.id} className="col s3" style={{ position: 'relative' }}>
              {isShowTrashCan && <div
                style={{
                  position: 'absolute', right: 0, top: -12, height: '1.5rem',
                  filter: 'invert(20%) sepia(87%) saturate(5763%) hue-rotate(329deg) brightness(90%) contrast(115%)'
                }}
                onClick={handleDeletePhoto}
              >
                {TrashCan}
              </div>}
              <Photo observationId={observation.id} url={data.url} />
            </div>
          ))}
          {photoURLs.map(({ url, isValid }, index) => (
            <div key={url} className="col s3" style={{ position: 'relative' }}>
              <div
                style={{ position: 'absolute', right: 0, top: -12, zIndex: 2 }}
                onClick={() => handleRemovePhoto(index)}
              >
                {Clear}
              </div>

              {!isValid ? <div
                style={{
                  position: 'absolute', right: 50, top: 28, height: '3rem',
                  zIndex: 2,
                  filter: 'invert(73%) sepia(80%) saturate(1120%) hue-rotate(358deg) brightness(105%) contrast(105%)'
                }}
              >
                {Warning}
              </div> : <></>}

              <img
                src={url}
                alt={`observation-${index}`}
                height="100px"
                width="100%"
                style={{ objectFit: 'cover', filter: !isValid ? 'grayscale(0.4) blur(5px)' : '' }}
              />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button
            aria-label="Add Photo"
            className="btn btn-tertiary"
            style={{ width: 'auto', display: 'flex', alignItems: 'center' }}
            onClick={handleAddPhotoClick}
          >
            {Add} <span style={{ marginLeft: 4 }}>Add Another Photo</span>
          </button>
          <button
            aria-label="Clear All Photos"
            className="btn btn-tertiary"
            style={{ width: 'auto' }}
            onClick={handleClearPhotos}
          >
            Clear All
          </button>
        </div>
      </div>
      <div className="row">
        <div className="input-field notes">
          <textarea
            disabled={!isObservationOwner(observation, user.attributes?.email)}
            id="notes"
            onChange={(event) =>
              setObservationProperties('notes', event.target.value)
            }
            value={observation.notes}
          />
          <label htmlFor="notes" className={observation.notes ? 'active' : ''}>
            Notes
          </label>
        </div>
      </div>
    </div>
  );

  const mapObsToServiceObj = () => {
    observation.habitatTypeProps = [
      {
        name: HabitatTypeProps.NumberOfYoung,
        value: young,
      } as IHabitatServiceTypeProp,
      {
        name: HabitatTypeProps.YoungAge,
        value: daysOld,
      } as IHabitatServiceTypeProp,
      {
        name: HabitatTypeProps.NumberOfFledged,
        value: fledged,
      } as IHabitatServiceTypeProp,
      {
        name: HabitatTypeProps.FledgedDate,
        value: fledgedDate,
      } as IHabitatServiceTypeProp,
    ];
    observation.quantity = parseInt(eggs);
    if (!observation.date) {
      observation.date = new Date();
    }
    return observation;
  };


  const saveObservation = async () => {
    setObservationProperties(
      'date',
      `${date} ${time}Z`.replace(' ', 'T') as string
    );

    let requestObj = mapObsToServiceObj();
    try {
      if (!(requestObj.habitatId && requestObj.locationId && requestObj.wildlifeId && requestObj.observationType)) {
        Toast.show('Location, habitat, observation type and wildlife are required to create an observation');
      } else {
        let newObservationId: number | undefined = undefined;
        if (type === 'create') {
          newObservationId = await ObservationsFormApiHelpers.postNewObservation(requestObj);
        } else {
          requestObj.photoUrls = existingPhotoURLs.map((x => x.url));
          await ObservationsFormApiHelpers.putEditedObservation(requestObj);
        }

        const id = observation?.id || newObservationId || undefined;
        if (photoURLs?.length && id) {
          const uploadPromises = photoURLs.filter(f => f.isValid).map(p => ObservationsFormApiHelpers.uploadObservationPhoto(id, p.photoFile))
          if (uploadPromises.length > 0)
            await Promise.all(uploadPromises)
        }
        sharedHistory.push(RouteNames.Observations);
      }
    } catch (err) {
      Toast.show('An error occurred when saving the observation');
    }
  };

  useEffect(() => {
    setSaveDisabled(() => {
      return !(eggs !== '' && young !== '' && fledged !== '' && observation.hasOwnProperty('habitatName') && observation.hasOwnProperty('observationType') && observation.hasOwnProperty('wildlifeName'))
    })
  }, [observation, eggs, young, fledged])

  return (
    <div className='observation-form'>
      <div className="title-info">
        <h4>{type === 'create' ? 'Create Observation' : 'Edit Observation'}</h4>
        {type === 'edit' && <p>Make changes to this observation</p>}
      </div>
      <div className="row">
        <Grid container spacing={0}>
          <Grid item xs>
            <div className="col s3">
              {type === 'edit' && (
                <h6 className="location">{observation.locationName}</h6>
              )}
            </div>
          </Grid>
          <Grid item xs>
            <div className="col s6"></div>
          </Grid>
          <Grid item xs alignItems='flex-end'>
            <Box display="flex" justifyContent="flex-end" className='saveCancelBox'>
              <div className="col">
                <button
                  aria-label="Cancel"
                  className="btn cancelBtn btn-tertiary"
                  onClick={() => sharedHistory.push(RouteNames.Home)}
                >
                  Cancel
                </button>
              </div>
              <div className="col">
                <button
                  aria-label="Save"
                  className="btn btn-primary"
                  onClick={saveObservation}
                  disabled={isLoading}
                >
                  Save
                </button>
              </div>
            </Box>
          </Grid>
        </Grid>
      </div>
      {type === 'create' &&
        (isLoading ? (
          <Preloader height="20rem" />
        ) : (
          <div className="row select generalData">
            <Select
              id="create-observation-location-select"
              value={observation.locationName ?? ''}
              label="Location"
              style={{ minWidth: 300 }}
              displayEmpty
              onChange={(event) => {
                setObservationProperties(
                  'locationName',
                  event.target.value as string
                );
                setObservationProperties(
                  'locationId',
                  locations?.find((l) => l.name === event.target.value)?.id
                );
                setObservationProperties('habitatName', '');
                setObservationProperties('habitatId', '');
              }}
            >
              <MenuItem value="" disabled>
                Choose your site
              </MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.name}>
                  {loc.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        ))}
      {observation.locationId && (

        <div className="row">
          <Grid container spacing={0}>
            <Grid item xs>
              <div className="col">{firstCol}</div>
            </Grid>
            <Grid item xs>
              <div className="col">{secondCol}</div>
            </Grid>
            <Grid item xs>
              <div className="col">{thirdCol}</div>
            </Grid>
          </Grid>

        </div>
      )}
    </div>
  );
};
